// src/screens/customer/checkout/OrderConfirmation.js

import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCheckout } from "../../../context/CheckoutContext";
import "../../../assets/css/customer/checkout/OrderConfirmation.css";
import { AiFillCheckSquare } from "react-icons/ai";
import { FaTruck } from "react-icons/fa";
import { FaBoxOpen } from "react-icons/fa";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaPhoneAlt } from "react-icons/fa";
import { SlCalender } from "react-icons/sl";
import AppContext from "../../../context/AppContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  CANCELLED: "cancelled",
};

const formatStatusLabel = (status) => {
  switch (status) {
    case STATUS.PENDING:
      return "Pending";
    case STATUS.CONFIRMED:
      return "Confirmed";
    case STATUS.CANCELLED:
      return "Cancelled";
    default:
      return status || "Processing";
  }
};

const statusBadgeClass = (status) => {
  switch (status) {
    case STATUS.CONFIRMED:
      return "success";
    case STATUS.CANCELLED:
      return "cancelled";
    case STATUS.PENDING:
    default:
      return "pending";
  }
};

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);
  const { orderId } = useParams();
  const { resetCheckout } = useCheckout();
  const [orderDetails, setOrderDetails] = useState(null);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [isPlacingOrder, setIsPlacingOrder] = useState(true);

  useEffect(() => {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    axios
      .post(`${baseUrl}/customer/order/getSessionType`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        // console.log(response.data);
        if (response.data.sessionType === "custom") {
          createCustomOrder();
        } else {
          createOrder();
        }
      })
      .catch((error) => {
        console.log(error);
        setIsPlacingOrder(false);
      });
  }, []);

  const createOrder = () => {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    axios
      .post(`${baseUrl}/customer/order/createOrder`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data.message);
        setOrderDetails(response.data.data);
        setIsPlacingOrder(false);
      })
      .catch((error) => {
        console.log(error);
        setIsPlacingOrder(false);
      });
  };

  const createCustomOrder = () => {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    axios
      .post(`${baseUrl}/customer/order/createCustomOrder`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setOrderDetails(response.data.data);
        setIsPlacingOrder(false);
      })
      .catch((error) => {
        console.log(error);
        setIsPlacingOrder(false);
      });
  };

  useEffect(() => {
    // Auto reset after 5 minutes
    const timer = setTimeout(
      () => {
        resetCheckout();
      },
      5 * 60 * 1000,
    );

    return () => clearTimeout(timer);
  }, [resetCheckout]);

  const handleContinueShopping = () => {
    resetCheckout();
    navigate("/products");
  };

  const handleViewOrders = () => {
    navigate("/customer/orders");
  };

  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">
        {/* Success Animation */}
        <div className="success-animation">
          <div className="checkmark-circle">
            {isPlacingOrder ? (
              <div className="order-loading-spinner"></div>
            ) : (
              <div className="checkmark">✓</div>
            )}
          </div>
        </div>

        {/* Success Message */}
        <h1 className="confirmation-title">
          {isPlacingOrder
            ? "Placing Your Order..."
            : "Order Placed Successfully!"}
        </h1>
        <p className="confirmation-subtitle">
          {isPlacingOrder
            ? "Please wait while we confirm your order."
            : "Your order has been placed! Our rider will collect payment on delivery."}
        </p>

        {/* Action Buttons */}
        <div className="confirmation-actions">
          <button className="btn btn-primary" onClick={handleViewOrders}>
            View Order Details
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleContinueShopping}
          >
            Continue Shopping
          </button>
        </div>

        {/* What's Next */}
        <div className="whats-next-section">
          <h3>What happens next?</h3>
          <div className="next-steps">
            <div className="step">
              <span className="step-icon">
                <FaBoxOpen />
              </span>
              <div className="step-content">
                <h4>Order Processing</h4>
                <p>We're preparing your items for shipment</p>
              </div>
            </div>
            <div className="step">
              <span className="step-icon">
                <FaTruck />
              </span>
              <div className="step-content">
                <h4>Shipping Updates</h4>
                <p>You'll receive updates on your order status</p>
              </div>
            </div>
            <div className="step">
              <span className="step-icon">
                <AiFillCheckSquare />
              </span>
              <div className="step-content">
                <h4>Delivery</h4>
                <p>Your order will arrive within 5-7 business days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="support-section">
          <p>Need help with your order?</p>
          <a href="/contact" className="support-link">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
